import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { rolesAPI } from '../lib/api';
import { 
  Users, 
  CheckCircle2, 
  Briefcase,
  ChevronDown,
  ChevronUp,
  Star,
  Plus,
  Trash2,
  Edit2,
  X,
  Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const RoleResponsibilities = () => {
  const { user, isManager } = useAuth();
  const { isDark } = useTheme();
  const [responsibilities, setResponsibilities] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedRole, setExpandedRole] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Modal states
  const [isAddRoleOpen, setIsAddRoleOpen] = useState(false);
  const [isEditRoleOpen, setIsEditRoleOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [newResponsibility, setNewResponsibility] = useState('');
  
  // Form states
  const [roleForm, setRoleForm] = useState({
    title: '',
    description: '',
    responsibilities: []
  });

  const fetchResponsibilities = async () => {
    try {
      const response = await rolesAPI.getAll();
      setResponsibilities(response.data);
      
      // Auto-expand user's role if they're an artist
      if (user?.role && !expandedRole) {
        const userRoleKey = Object.keys(response.data).find(
          key => key.toLowerCase().includes(user.role.toLowerCase()) ||
                 response.data[key].title.toLowerCase().includes(user.role.toLowerCase())
        );
        if (userRoleKey) {
          setExpandedRole(userRoleKey);
        }
      }
    } catch (error) {
      console.error('Error fetching responsibilities:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResponsibilities();
  }, []);

  const toggleRole = (roleKey) => {
    if (!editMode) {
      setExpandedRole(expandedRole === roleKey ? null : roleKey);
    }
  };

  const getRoleColor = (index) => {
    const colors = [
      { bg: 'bg-violet-500', light: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-600 dark:text-violet-400' },
      { bg: 'bg-blue-500', light: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
      { bg: 'bg-emerald-500', light: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400' },
      { bg: 'bg-amber-500', light: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
      { bg: 'bg-rose-500', light: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-600 dark:text-rose-400' },
      { bg: 'bg-cyan-500', light: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-600 dark:text-cyan-400' },
      { bg: 'bg-indigo-500', light: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-600 dark:text-indigo-400' },
    ];
    return colors[index % colors.length];
  };

  const isUserRole = (roleTitle) => {
    if (!user?.role) return false;
    return roleTitle.toLowerCase().includes(user.role.toLowerCase()) ||
           user.role.toLowerCase().includes(roleTitle.toLowerCase().split(' ')[0]);
  };

  // Add new role
  const handleAddRole = async () => {
    if (!roleForm.title.trim()) {
      toast.error('Role title is required');
      return;
    }
    
    try {
      await rolesAPI.create({
        role_key: roleForm.title,
        title: roleForm.title,
        description: roleForm.description,
        responsibilities: roleForm.responsibilities.filter(r => r.trim())
      });
      toast.success('Role added successfully');
      setIsAddRoleOpen(false);
      setRoleForm({ title: '', description: '', responsibilities: [] });
      fetchResponsibilities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add role');
    }
  };

  // Edit role
  const openEditRole = (roleKey, roleData) => {
    setSelectedRole(roleKey);
    setRoleForm({
      title: roleData.title,
      description: roleData.description || '',
      responsibilities: [...roleData.responsibilities]
    });
    setIsEditRoleOpen(true);
  };

  const handleUpdateRole = async () => {
    try {
      await rolesAPI.update(selectedRole, {
        title: roleForm.title,
        description: roleForm.description,
        responsibilities: roleForm.responsibilities.filter(r => r.trim())
      });
      toast.success('Role updated successfully');
      setIsEditRoleOpen(false);
      fetchResponsibilities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  // Delete role
  const handleDeleteRole = async (roleKey) => {
    if (!confirm('Are you sure you want to delete this role?')) return;
    
    try {
      await rolesAPI.delete(roleKey);
      toast.success('Role deleted successfully');
      fetchResponsibilities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  // Add responsibility inline
  const handleAddResponsibility = async (roleKey) => {
    if (!newResponsibility.trim()) return;
    
    try {
      await rolesAPI.addResponsibility(roleKey, newResponsibility.trim());
      toast.success('Responsibility added');
      setNewResponsibility('');
      fetchResponsibilities();
    } catch (error) {
      toast.error('Failed to add responsibility');
    }
  };

  // Delete responsibility inline
  const handleDeleteResponsibility = async (roleKey, index) => {
    try {
      await rolesAPI.deleteResponsibility(roleKey, index);
      toast.success('Responsibility removed');
      fetchResponsibilities();
    } catch (error) {
      toast.error('Failed to remove responsibility');
    }
  };

  // Add/remove responsibility in form
  const addResponsibilityToForm = () => {
    setRoleForm(prev => ({
      ...prev,
      responsibilities: [...prev.responsibilities, '']
    }));
  };

  const updateResponsibilityInForm = (index, value) => {
    setRoleForm(prev => ({
      ...prev,
      responsibilities: prev.responsibilities.map((r, i) => i === index ? value : r)
    }));
  };

  const removeResponsibilityFromForm = (index) => {
    setRoleForm(prev => ({
      ...prev,
      responsibilities: prev.responsibilities.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  const roleEntries = Object.entries(responsibilities);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="role-responsibilities-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={cn(
            "text-2xl md:text-3xl font-bold font-outfit",
            isDark ? "text-white" : "text-slate-800"
          )}>
            Roles & Responsibilities
          </h1>
          <p className={cn("mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
            Understand your role and what's expected from each team member
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isManager() && (
            <>
              <Button
                variant={editMode ? "default" : "outline"}
                onClick={() => setEditMode(!editMode)}
                className={editMode ? "bg-amber-500 hover:bg-amber-600" : ""}
              >
                <Edit2 className="w-4 h-4 mr-2" />
                {editMode ? 'Exit Edit Mode' : 'Edit Mode'}
              </Button>
              <Button
                onClick={() => {
                  setRoleForm({ title: '', description: '', responsibilities: [''] });
                  setIsAddRoleOpen(true);
                }}
                className="bg-[#331062] hover:bg-[#4a1d8a]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Role
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Edit Mode Banner */}
      {editMode && (
        <Card className="border-amber-500 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="p-4">
            <p className="text-sm text-amber-700 dark:text-amber-300">
              <strong>Edit Mode Active:</strong> Click on responsibilities to delete them, or use the + button to add new ones.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Roles Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {roleEntries.map(([roleKey, roleData], index) => {
          const isExpanded = expandedRole === roleKey || editMode;
          const isCurrentUserRole = isUserRole(roleData.title);
          const color = getRoleColor(index);

          return (
            <motion.div
              key={roleKey}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card 
                className={cn(
                  "transition-all duration-200 overflow-hidden",
                  !editMode && "cursor-pointer",
                  isExpanded && "ring-2 ring-[#331062]",
                  isCurrentUserRole && !editMode && "ring-2 ring-[#CCFF00]",
                  isDark ? "bg-slate-800 border-slate-700 hover:bg-slate-750" : "hover:shadow-md"
                )}
                onClick={() => toggleRole(roleKey)}
                data-testid={`role-card-${roleKey.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center",
                        color.bg
                      )}>
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className={cn(
                          "text-lg font-semibold flex items-center gap-2",
                          isDark ? "text-white" : "text-slate-800"
                        )}>
                          {roleData.title}
                          {isCurrentUserRole && (
                            <Star className="w-4 h-4 text-[#CCFF00] fill-[#CCFF00]" />
                          )}
                        </CardTitle>
                        <p className={cn(
                          "text-xs",
                          isDark ? "text-slate-400" : "text-slate-500"
                        )}>
                          {roleData.responsibilities?.length || 0} responsibilities
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editMode && isManager() && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditRole(roleKey, roleData);
                            }}
                            className="h-8 w-8"
                          >
                            <Edit2 className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRole(roleKey);
                            }}
                            className="h-8 w-8"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </>
                      )}
                      {!editMode && (
                        <div className={cn(
                          "p-2 rounded-full transition-colors",
                          isDark ? "hover:bg-slate-700" : "hover:bg-slate-100"
                        )}>
                          {isExpanded ? (
                            <ChevronUp className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                          ) : (
                            <ChevronDown className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <CardContent className="pt-0 pb-4">
                        {/* Description */}
                        <p className={cn(
                          "text-sm mb-4 pb-3 border-b",
                          isDark ? "text-slate-400 border-slate-700" : "text-slate-600 border-slate-100"
                        )}>
                          {roleData.description || 'No description provided.'}
                        </p>

                        {/* Responsibilities List */}
                        <div className="space-y-2">
                          {roleData.responsibilities?.map((resp, respIndex) => (
                            <motion.div
                              key={respIndex}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: respIndex * 0.03 }}
                              className={cn(
                                "flex items-start gap-3 p-2 rounded-lg group",
                                isDark ? "bg-slate-700/50" : color.light
                              )}
                            >
                              <CheckCircle2 className={cn("w-4 h-4 mt-0.5 flex-shrink-0", color.text)} />
                              <span className={cn(
                                "text-sm flex-1",
                                isDark ? "text-slate-200" : "text-slate-700"
                              )}>
                                {resp}
                              </span>
                              {editMode && isManager() && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteResponsibility(roleKey, respIndex);
                                  }}
                                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3 text-red-500" />
                                </Button>
                              )}
                            </motion.div>
                          ))}
                          
                          {/* KPIs Section */}
                          {roleData.kpis && roleData.kpis.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-dashed border-slate-300 dark:border-slate-600">
                              <h4 className={cn(
                                "text-sm font-semibold mb-2 flex items-center gap-2",
                                isDark ? "text-slate-200" : "text-slate-700"
                              )}>
                                <Briefcase className="w-4 h-4" />
                                Key Performance Indicators
                              </h4>
                              <div className="space-y-2">
                                {roleData.kpis.map((kpi, kpiIndex) => (
                                  <motion.div
                                    key={kpiIndex}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: kpiIndex * 0.03 }}
                                    className={cn(
                                      "flex items-start gap-3 p-2 rounded-lg group",
                                      isDark ? "bg-slate-700/30" : "bg-blue-50 dark:bg-blue-900/20"
                                    )}
                                  >
                                    <Star className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" />
                                    <span className={cn(
                                      "text-sm flex-1",
                                      isDark ? "text-slate-200" : "text-slate-700"
                                    )}>
                                      {kpi}
                                    </span>
                                  </motion.div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Add Responsibility Input */}
                          {editMode && isManager() && (
                            <div className="flex gap-2 mt-3 pt-3 border-t border-dashed border-slate-300 dark:border-slate-600">
                              <Input
                                placeholder="Add new responsibility..."
                                value={expandedRole === roleKey ? newResponsibility : ''}
                                onChange={(e) => {
                                  setExpandedRole(roleKey);
                                  setNewResponsibility(e.target.value);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    handleAddResponsibility(roleKey);
                                  }
                                }}
                                className="flex-1"
                              />
                              <Button
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddResponsibility(roleKey);
                                }}
                                className="bg-[#331062] hover:bg-[#4a1d8a]"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Summary Footer */}
      <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>
                {roleEntries.length}
              </p>
              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Total Roles</p>
            </div>
            <div>
              <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>
                {roleEntries.reduce((acc, [_, data]) => acc + (data.responsibilities?.length || 0), 0)}
              </p>
              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Total Duties</p>
            </div>
            <div>
              <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>
                {roleEntries.length > 0 ? Math.round(roleEntries.reduce((acc, [_, data]) => acc + (data.responsibilities?.length || 0), 0) / roleEntries.length) : 0}
              </p>
              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Avg per Role</p>
            </div>
            <div>
              <p className={cn("text-2xl font-bold text-[#331062]", isDark && "text-[#CCFF00]")}>
                {user?.role || 'N/A'}
              </p>
              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Your Role</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Role Modal */}
      <Dialog open={isAddRoleOpen} onOpenChange={setIsAddRoleOpen}>
        <DialogContent className={cn("max-w-lg", isDark && "bg-slate-800 border-slate-700")}>
          <DialogHeader>
            <DialogTitle className={isDark ? "text-white" : ""}>Add New Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className={cn("text-sm font-medium", isDark ? "text-slate-300" : "text-slate-700")}>
                Role Title *
              </label>
              <Input
                value={roleForm.title}
                onChange={(e) => setRoleForm({ ...roleForm, title: e.target.value })}
                placeholder="e.g., Senior Designer"
                className="mt-1"
              />
            </div>
            <div>
              <label className={cn("text-sm font-medium", isDark ? "text-slate-300" : "text-slate-700")}>
                Description
              </label>
              <Textarea
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                placeholder="Brief description of the role..."
                className="mt-1"
                rows={2}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={cn("text-sm font-medium", isDark ? "text-slate-300" : "text-slate-700")}>
                  Responsibilities
                </label>
                <Button variant="ghost" size="sm" onClick={addResponsibilityToForm}>
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {roleForm.responsibilities.map((resp, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={resp}
                      onChange={(e) => updateResponsibilityInForm(index, e.target.value)}
                      placeholder={`Responsibility ${index + 1}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeResponsibilityFromForm(index)}
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddRoleOpen(false)}>Cancel</Button>
            <Button onClick={handleAddRole} className="bg-[#331062] hover:bg-[#4a1d8a]">
              <Save className="w-4 h-4 mr-2" /> Save Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Modal */}
      <Dialog open={isEditRoleOpen} onOpenChange={setIsEditRoleOpen}>
        <DialogContent className={cn("max-w-lg", isDark && "bg-slate-800 border-slate-700")}>
          <DialogHeader>
            <DialogTitle className={isDark ? "text-white" : ""}>Edit Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className={cn("text-sm font-medium", isDark ? "text-slate-300" : "text-slate-700")}>
                Role Title
              </label>
              <Input
                value={roleForm.title}
                onChange={(e) => setRoleForm({ ...roleForm, title: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <label className={cn("text-sm font-medium", isDark ? "text-slate-300" : "text-slate-700")}>
                Description
              </label>
              <Textarea
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                className="mt-1"
                rows={2}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={cn("text-sm font-medium", isDark ? "text-slate-300" : "text-slate-700")}>
                  Responsibilities
                </label>
                <Button variant="ghost" size="sm" onClick={addResponsibilityToForm}>
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {roleForm.responsibilities.map((resp, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={resp}
                      onChange={(e) => updateResponsibilityInForm(index, e.target.value)}
                      placeholder={`Responsibility ${index + 1}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeResponsibilityFromForm(index)}
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditRoleOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateRole} className="bg-[#331062] hover:bg-[#4a1d8a]">
              <Save className="w-4 h-4 mr-2" /> Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleResponsibilities;
